import { Injectable } from '@angular/core';
import { Course } from '@core/models/course/course.model';
import { forkJoin, Observable, of, pipe } from 'rxjs';
import { CourseApiService } from '@core/api/services/course-api.service';
import { Series, SeriesAdapter } from '@core/models/course/series.model';
import { map, switchMap } from 'rxjs/operators';
import { EnrolmentApiService } from '@core/api/services/enrolment-api.service';
import { SeriesApiService } from '@core/api/services/series-api.service';
import { SeriesModel } from '@core/api/models/course/series.model';
import { CourseTask, CourseTaskAdapter } from '@core/models/course/course-task.model';
import { TaskApiService } from '@core/api/services/task.service';
import { TaskModel } from '@core/api/models/course/task-model';
import { Pageable } from '@core/pagination/pageable';
import { FormQuestion } from '@core/models/form-question';
import { CourseListItem, CourseListItemAdapter, SeriesListItem } from '@core/models/course/course-list-item.model';
import { MediaFile } from '@core/models/media-file.model';
import { Certificate } from '@core/models/enrolment/certificate.model';
import { MediaAdapter } from '@core/adapters/media.adapter';
import { DashboardSeries } from '@core/models/dashboard/dashboard-series.model';
import { CourseOverviewItemAdapter, CourseOverviewResult } from '@core/models/course/course-overview-item.model';
import { DashboardSeriesAttendanceAdapter } from '@core/adapters/dashboard-series-attendance.adapter';
import { SessionApiService } from '@core/api/services/session-api.service';
import { SessionAdapter } from '@core/models/course/session.model';
import { TrainingPlanCourseDateApiService } from '@core/api/services/training-plan-course-date-api.service';
import { PageableFactory } from '@core/pagination/pageable.factory';
import { formatDate } from '@angular/common';

@Injectable({
    providedIn: 'root'
  })
  export class CourseService {
    constructor(
        private courseApiService:CourseApiService,
        private seriesApiService:SeriesApiService,
        private enrolmentApiService:EnrolmentApiService,
        private coursesApiService:CourseApiService,
        private seriesAdapter:SeriesAdapter,
        private taskApiService:TaskApiService,
        private courseTaskAdapter:CourseTaskAdapter,
        private courseListItemAdapter:CourseListItemAdapter,
        private mediaFileAdapter:MediaAdapter,
        private sessionApiService:SessionApiService,
        private sessionAdapter:SessionAdapter,
        private trainingPlanCourseDateApiService:TrainingPlanCourseDateApiService,
        private dashboardSeriesAttendanceAdapter:DashboardSeriesAttendanceAdapter,
        private courseOverviewItemAdapter:CourseOverviewItemAdapter
        ){}

    // Unified course overview (branch + training plan, merged/sorted server-side). scope=current for the cards,
    // scope=finished (paged) for the dashboard table, scope=null for the full paged list (the /courses page).
    // Replaces the four getDashboardSeries / *HistorySeries calls.
    getCourseOverview(scope:'current'|'finished'|null = null, page:number = 1, limit:number = 6, search:string = ''):Observable<CourseOverviewResult>{
        return this.coursesApiService.getDashboardCourses(scope, page, limit, search).pipe(
            map((x:any) => ({
                items: (x.items ?? []).map((i:any) => this.courseOverviewItemAdapter.adapt(i)),
                total: x.total ?? 0,
                page: x.page ?? page,
                limit: x.limit ?? limit
            }))
        );
    }

    getById(shortUrl:string,enrolementCheck:boolean = false):Observable<Course>{


    let calls = {course:this.courseApiService.get(shortUrl)};
    if(enrolementCheck){
        calls = {...calls,...{enrolements: this.enrolmentApiService.list({'series.course.id': shortUrl, pagination: 'false'})}};
    }
     return forkJoin(calls).pipe(
         switchMap(
         (x:any) =>
            {
                let course = new Course(x.course.id,x.course.name,x.course.series.map((serie:SeriesModel) => this.seriesAdapter.adapt(serie)),x.course.requirementsMet, x.course.description??'');
                if(x.enrolements){
                    const connectedBranch = x.course.branches.find((y:any) => y.shortUrl == shortUrl);
                    course.available = connectedBranch.active;
                   // this.branchService.setBranch(connectedBranch.branch.id);
                    if(x.enrolements.totalItems){
                        course.series.forEach(serie => {
                           // serie.enrolled = !!x.enrolements.members.find((v: EnrolmentModel) => !!v.series.find((s: SeriesModel) => s.id === serie.id))
                            //serie.enrolled = true;
                        });
                    }
                }
                return of(course);
      }));

    }

    getSeriesByCourseId(id:string):Observable<Array<Series>>{
        return this.courseApiService.getSeries(id).pipe(
            switchMap(
                (x:any) =>{
                    let series = x.members.map((m:any) => this.seriesAdapter.adapt(m));
                    return of(series);
                }
            )
        )
    }

    getDashboardSeries(minified:boolean = false):Observable<DashboardSeries[]>{

        return this.getCourses(minified).pipe(
            map(
                (x) => {

                    let series:DashboardSeries[] = []
                    x.runningCourses.forEach(cl => {
                       series = series.concat(cl.series.filter(si => si.nextSession && !si.nextSession?.inThePast).map((s:SeriesListItem) =>{
                            const di = {
                                courseId:s.id,
                                courseName: cl.name,
                                cover:cl.cover,
                                seriesName:s.name,
                                sessionTotal:s.sessionCount,
                                sessionId:s.nextSession?.id,
                                sessionName:s.nextSession?.name,
                                sessionDate:s.nextSession?.date,
                                sessionStartTime:s.nextSession?.startTime,
                                sessionEndTime:s.nextSession?.endTime,
                                sessionLocation:s.nextSession?.location,
                                branchName:cl.branchName,
                                branchLogo:cl.branchLogo,
                                branchColor:cl.branchColor?cl.branchColor:'#0b145a',
                                branchUrl:'',
                                organisationId:'',
                                organisationName:'',
                                organisationLogo:'',
                                isTrainingPlan:false,
                                courseLocation:'',
                                courseLocationName:''
                            } as DashboardSeries
                            return di;
                        })
                        );
                    });

                    return series;
                }
            )
        )
    }

    getTrainingPlanDashboardSeries(userId:string):Observable<DashboardSeries[]>{
        const today = new Date();
        const futureEnd = new Date();
        futureEnd.setMonth(futureEnd.getMonth() + 12);

        const params: Record<string, string> = {
            'exists[member]': 'true',
            'order[date]': 'ASC',
            'pagination': 'false',
            'member.trainingPlanCourse.dateType[0]': 'static_dates',
            'member.trainingPlanCourse.dateType[1]': 'range',
            'member.profileMember.userProfile.id': userId,
            'date[after]': formatDate(today, 'yyyy-MM-dd', 'nl_BE'),
            'date[before]': formatDate(futureEnd, 'yyyy-MM-dd', 'nl_BE')
        };

        return this.fetchTrainingPlanSeries(params, false);
    }

    getTrainingPlanHistorySeries(userId:string):Observable<DashboardSeries[]>{
        const today = new Date();
        const pastStart = new Date();
        pastStart.setMonth(pastStart.getMonth() - 24);

        const params: Record<string, string> = {
            'exists[member]': 'true',
            'order[date]': 'DESC',
            'pagination': 'false',
            'member.trainingPlanCourse.dateType[0]': 'static_dates',
            'member.trainingPlanCourse.dateType[1]': 'range',
            'member.profileMember.userProfile.id': userId,
            'date[after]': formatDate(pastStart, 'yyyy-MM-dd', 'nl_BE'),
            'date[before]': formatDate(today, 'yyyy-MM-dd', 'nl_BE')
        };

        return this.fetchTrainingPlanSeries(params, true);
    }

    // descending=true keeps the most recent session per course (history), false keeps the earliest (upcoming).
    private fetchTrainingPlanSeries(params: Record<string, string>, descending:boolean):Observable<DashboardSeries[]>{
        return this.trainingPlanCourseDateApiService.list(params).pipe(
            map(collection => {
                const items = (collection.members ?? [])
                    .filter((x:any) => x.day && x.month && x.year)
                    .map((x:any) => this.dashboardSeriesAttendanceAdapter.adapt(x));

                const grouped = new Map<string, DashboardSeries>();
                items.forEach((item:DashboardSeries) => {
                    const existing = grouped.get(item.courseId);
                    if (existing) {
                        existing.sessionTotal++;
                        if (existing.courseDateType === 'range'){
                            const endBound = descending
                                ? (item.sessionDate < (existing.sessionEndDate ?? existing.sessionDate))
                                : (item.sessionDate > (existing.sessionEndDate ?? existing.sessionDate));
                            if (endBound) {
                                existing.sessionEndDate = item.sessionDate;
                            }
                        }
                    } else {
                        item.sessionTotal = 1;
                        grouped.set(item.courseId, item);
                    }
                });
                return Array.from(grouped.values());
            })
        );
    }

    // Adapts the finished half of getCourses() into DashboardSeries so it can be merged with training plan history.
    getFinishedDashboardSeries(minified:boolean = true):Observable<DashboardSeries[]>{
        return this.getCourses(minified).pipe(
            map(x => {
                const series:DashboardSeries[] = [];
                x.finishedCourses.forEach(cl => {
                    cl.series.forEach((s:SeriesListItem) => {
                        series.push({
                            courseId: s.id,
                            courseName: cl.name,
                            cover: cl.cover,
                            seriesName: s.name,
                            sessionTotal: s.sessionCount,
                            sessionId: s.nextSession?.id ?? '',
                            sessionName: s.nextSession?.name ?? '',
                            sessionDate: s.nextSession?.date as any,
                            sessionEndDate: null,
                            sessionStartTime: s.nextSession?.startTime ?? '',
                            sessionEndTime: s.nextSession?.endTime ?? '',
                            sessionLocation: s.nextSession?.location as any,
                            branchName: cl.branchName,
                            branchLogo: cl.branchLogo,
                            branchColor: cl.branchColor ? cl.branchColor : '#0b145a',
                            branchUrl: '',
                            organisationId: '',
                            organisationName: '',
                            organisationLogo: '',
                            isTrainingPlan: false,
                            courseLocation: '',
                            courseLocationName: '',
                            courseDateType: ''
                        } as DashboardSeries);
                    });
                });
                return series;
            })
        );
    }

    getCourses(minified:boolean = false):Observable<{finishedCourses:CourseListItem[],runningCourses:CourseListItem[]}>{
        return this.coursesApiService.getCourses(minified).pipe(
            map(
                (x:any) =>{
                    return {
                        finishedCourses: (x.finishedCourses??[]).map((m:any) => this.courseListItemAdapter.adapt(m)),
                        runningCourses: (x.runningCourses??[]).map((m:any) => this.courseListItemAdapter.adapt(m))
                    }
                }
            )
        )
    }

    getSeriesById(id:string):Observable<Series>{
        return this.seriesApiService.get(id).pipe(switchMap(
            (x:SeriesModel) =>{
                return of(this.seriesAdapter.adapt(x));
            }
        ));
    }

    getTasks(seriesId:string):Observable<Pageable<CourseTask>>{

        return this.taskApiService.list(seriesId).pipe(switchMap(
            (list:any) =>{
                const p = list.pagination;
                const result:Pageable<CourseTask> = new Pageable(list.items.map((t:TaskModel) => this.courseTaskAdapter.adapt(t)), p.currentPage, p.totalPages, p.totalItems, p.itemsPerPage)
                return of(result)
            }
        ));
    }

    getTask(id:string):Observable<CourseTask>{
        return this.taskApiService.get(id).pipe(switchMap(
            (x:TaskModel) =>{
                return of(this.courseTaskAdapter.adapt(x));
            }
        ))
    }

  getGlobalTask(id:string):Observable<CourseTask>{
    return this.taskApiService.getGlobal(id).pipe(switchMap(
      (x:TaskModel) =>{
        return of(this.courseTaskAdapter.adapt(x));
      }
    ))
  }

    getTaskSummary(id: string): Observable<any> {
      return this.taskApiService.getFeedbackSummary(id);
    }

    uploadTask(id:string,file:File){
        return this.taskApiService.uploadTask(id,file);
    }

    getQuestionFeedback(q:FormQuestion){
        return this.taskApiService.getQuestionFeedback(q.id, q.answer);
    }

    turnInTask(task:CourseTask, questions:FormQuestion[], started:Date){
        return this.taskApiService.turnIn(task, questions, started);
    }

    getFiles(seriesId:string):Observable<MediaFile[]>{
        return this.courseApiService.getFiles(seriesId).pipe(switchMap(
            (x:any) =>{
                return of(x.map((m:any) =>
                this.mediaFileAdapter.adaptFromCustom(m)));
            }
        ))
    }
    getAttesten(seriesId:string):Observable<Certificate[]>{
        return this.courseApiService.getCertificates(seriesId).pipe(switchMap(
            (x:any) =>{
                return of(x.map((m:any) =>
                this.mediaFileAdapter.adaptFromCustom(m)));
            }
        ))
    }
    getPersonalAttesten():Observable<Certificate[]>{
        return this.courseApiService.getPersonalCertificates().pipe(switchMap(
            (x:any) =>{
                return of(x.map((m:any) =>
                this.mediaFileAdapter.adaptFromCustom(m)));
            }
        ))
    }
    getPersonalFiles():Observable<MediaFile[]>{
        return this.courseApiService.getPersonalFiles().pipe(switchMap(
            (x:any) =>{
                return of(x.map((m:any) =>
                this.mediaFileAdapter.adaptFromCustom(m)));
            }
        ))
    }

    getSessionById(id:string){
        return this.sessionApiService.get(id).pipe(map(s => this.sessionAdapter.adapt(s) ))
    }
  }
